import { ServiceWithSession } from './base-service';
import type { Entity, Property } from '@cognitoforms/model.js';
import { isEntityType } from '@cognitoforms/model.js';
import { Deferred } from 'src/util/deferred';
import type { Quantity } from '@cognitoforms/types/server-types/forms/model/quantity';
import type { FormSession } from './form-session';
import { visitEntity, visitType } from 'src/util/model';
import { hasProp } from 'src/util/helpers';

const DEBOUNCE_RATE = 100;
const QTY_SELECTED = '_QuantitySelected';
const QTY_USED = '_QuantityUsed';
const QTY_LIMIT = '_QuantityLimit';
const QTY_LIMIT_CALC = '_QuantityLimitCalculated';
const QTY_FORMAT = '_QuantityKeyFormat';

export type QuantityData = {
	key: string;
	count: number;
	countIsLimit: boolean;
}

export default class QuantityService extends ServiceWithSession {
	private pendingRefresh: Promise<Quantity[]>;
	private initialState: Map<string, number> = null;
	private initialized: Deferred = new Deferred();
	private entity: Entity;

	constructor(hasInitialState: boolean, session: FormSession) {
		super(session);

		if (hasInitialState)
			this.initialState = new Map();
	}

	initialize(root: Entity) {
		this.entity = root;
		const watchedProps = [];
		visitType(root.meta.type, (t, path) => {
			for (const prop of t.properties) {
				if (t.properties.some(otherProp => otherProp.name === prop.name + QTY_FORMAT) || prop.isList && isEntityType(prop.propertyType)) {
					let propPath = path;
					if (path.length)
						propPath += '.';

					propPath += prop.name;
					watchedProps.push(root.meta.type.getPath(propPath));
				}
			}
		});

		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const qs = this;
		root.meta.type.addRule({
			execute() {
				if (this !== qs.entity)
					return;

				qs.refresh();
			},
			onChangeOf: watchedProps
		}).register();

		if (this.initialState)
			this.initialState = this.getState();

		this.refresh();
	}

	async reset({ hasInitialState, root }: { hasInitialState: boolean; root: Entity }) {
		this.entity = root;
		if (hasInitialState)
			this.initialState = this.getState();
		else
			this.initialState = null;

		await this.refresh();
	}

	private getNormalizedPropertyValue(entity: Entity, prop: Property): string {
		let fieldValue = prop.value(entity);
		if (fieldValue && typeof fieldValue === 'string')
			fieldValue = fieldValue.trim();

		const result = entity.serializer.serializePropertyValue(entity, prop, fieldValue, { force: true, useAliases: false });

		if (!result || result.value === null)
			return '';

		return `${result ? result.value : result}`
			.replace(/Σ([_\-,!?\s\d]+|$)/g, 'σ$1') // JS toLowerCase() is inconsistent with C# toLower() for Greek when a string ends with upper case sigma. https://en.wikipedia.org/wiki/Sigma
			.toLowerCase();
	}

	/**
	 * Updates `QuantityUsed` fields on tracked entities by combining the provided quantity data with the quantity data
	 * currently represented by tracked entities.
	 */
	private updateQuantities(quantityData: QuantityData[]) {
		const updates = [];
		const usedQty = {};
		const limits = {};

		for (const { key, count, countIsLimit } of quantityData.filter(d => d !== null)) {
			if (countIsLimit)
				limits[key] = count;
			else
				usedQty[key] = (usedQty[key] || 0) + count;
		}

		this.visitLimitedProperties((e, prop) => {
			const key = this.getKeyFor(e, prop);
			const value = prop.value(e);

			// If value cannot be limited or the limit is null ignore the validation
			if (!this.canLimitValue(value) || e[prop.name + QTY_LIMIT] === null) {
				updates.push(() => (e[prop.name + QTY_USED] = 0));
			}
			else {
				usedQty[key] = (usedQty[key] || 0) + e[prop.name + QTY_SELECTED];

				// Once we're done collecting all the quantity information, update the model
				updates.push(() => {
					const offset = (this.initialState ? this.initialState[key] : 0) || 0;
					e[prop.name + QTY_USED] = usedQty[key] - offset;

					// set the limit based on server data if we have it
					if (hasProp(limits, key))
						e[prop.name + QTY_LIMIT] = limits[key];
					// keep limit updated with calculated value for "normal" fields
					else
						e[prop.name + QTY_LIMIT] = e[prop.name + QTY_LIMIT_CALC];
				});
			}
		});

		updates.forEach(run => run());
	}

	private canLimitValue(value: any) {
		return value !== null
			&& value !== ''
			&& value !== false;
	}

	private visitLimitedProperties(callback: (e: Entity, p: Property) => void) {
		visitEntity(this.entity, (e, prop) => {
			const format = e[prop.name + QTY_FORMAT];
			if (format !== undefined)
				callback(e, prop);
		}, { followCircularProperties: false, followLookups: false });
	}

	getState(): Map<string, number> {
		const state = new Map<string, number>();
		this.visitLimitedProperties((e, p) => {
			const key = this.getKeyFor(e, p);
			state[key] = (state[key] || 0) + e[p.name + QTY_SELECTED];
		});

		return state;
	}

	get ready() {
		return this.initialized.promise;
	}

	get loadingComplete() {
		return this.pendingRefresh;
	}

	/**
	 * Get a key that can be used to load quantity information for a specific property and value combination.
	 * @param prop The model property that is quantity limited.
	 * @param value The normalized value of the property.
	 */
	private getKeyFor(entity: Entity, prop: Property) {
		const keyFormat = entity[prop.name + QTY_FORMAT];
		const normalizedValue = this.getNormalizedPropertyValue(entity, prop);
		return keyFormat.replace('{value}', normalizedValue);
	}

	/**
	 * Initiates a service request for quantity records. Multiple calls within a short time frame will be batched together.
	 * The remote quantity information is then used to update the `QuantityUsed` properties associated with all tracked
	 * fields.
	 */
	refresh() {
		if (!this.pendingRefresh) {
			const task = new Deferred();
			this.pendingRefresh = task.promise;

			const updates = [];
			this.visitLimitedProperties((e, prop) => {
				const value = prop.value(e);

				// If value cannot be limited or the limit is null ignore the validation
				if (!this.canLimitValue(value) || e[prop.name + QTY_LIMIT] === null)
					updates.push(() => (e[prop.name + QTY_USED] = 0));
			});

			updates.forEach(run => run());

			setTimeout(async () => {
				let success = false;
				try {
					const result = await this.refreshQuantitiesRequest();
					success = true;
					this.updateQuantities(result);
				}
				catch (err) { }

				this.pendingRefresh = null;

				if (this.initialized.pending)
					this.initialized.resolve();

				if (success)
					task.resolve();
				else
					task.reject();
			}, DEBOUNCE_RATE);
		}

		return this.pendingRefresh;
	}

	async refreshQuantitiesRequest() {
		let result;
		const state = this.getState();
		const keys = Object.keys(state);
		if (keys && keys.length)
			result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/quantity-limit/load-quantity-data',
				data: { keys }
			});
		else
			return [];

		if (result.response && result.response.data)
			return result.response.data as QuantityData[];

		throw this.parseError(result.error);
	}
}